import { z } from 'zod';

/** The PROM instruments the Clinical Platform records. */
export enum PlatformPromCode {
  Mycaw = 'mycaw',
  Distress = 'distress',
  Concerns = 'concerns',
}

/**
 * MYCaW pairs an initial form, where the member names their own concerns, with
 * retests that re-rate those same concerns. Instruments without that pairing
 * (the Distress Thermometer, the Concerns Cloud) report a null kind.
 */
export enum PlatformPromSubmissionKind {
  Initial = 'initial',
  FollowUp = 'follow-up',
}

/**
 * A single answer within a submission. An item carries a score or a free-text
 * value, never both: the platform derives them from a FHIR Observation
 * component, whose value is a choice type.
 */
export const PlatformPromItemSchema = z.object({
  code: z.string(),
  label: z.string(),
  score: z.number().nullable(),
  value: z.string().nullable(),
});

export type PlatformPromItem = z.infer<typeof PlatformPromItemSchema>;

export const PlatformPromSubmissionSchema = z.object({
  id: z.string(),
  recordedAt: z.string(),
  kind: z.enum(PlatformPromSubmissionKind).nullable(),
  items: z.array(PlatformPromItemSchema),
});

export type PlatformPromSubmission = z.infer<
  typeof PlatformPromSubmissionSchema
>;

export const PlatformPromSchema = z.object({
  prom: z.enum(PlatformPromCode),
  display: z.string(),
  scale: z.object({ min: z.number(), max: z.number() }).nullable(),
  /** Newest first. */
  submissions: z.array(PlatformPromSubmissionSchema),
});

export type PlatformProm = z.infer<typeof PlatformPromSchema>;

export const PlatformPromsSchema = z.array(PlatformPromSchema);
